import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Divider,
  Grid,
  Container,
  Paper,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
  alpha,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import UpdateIcon from "@mui/icons-material/Update";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FeedbackIcon from "@mui/icons-material/Feedback";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { OverAllLoader } from "../../components/OverAllLoader";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import dayjs from "dayjs";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Premium color palette
const themeColors = (() => {
  const primary = "#1976d2"; // your main primary color
  const surface = "#ffffff";
  const background = "#fafbfe";

  // Helper to lighten/darken a hex color
  const adjustColor = (color, percent) => {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      "#" +
      (
        0x1000000 +
        (R < 255 ? (R < 0 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 0 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 0 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  };

  return {
    primary: primary,
    primaryLight: adjustColor(primary, 40),
    primaryDark: adjustColor(primary, -20),
    success: "#2e7d32",
    successLight: "#e8f5e9",
    error: "#d32f2f",
    errorLight: "#ffebee",
    warning: "#ed6c02",
    background: background,
    surface: surface,
    textPrimary: "#1a237e",
    textSecondary: "#5f6368",
    border: "#e0e0e0",

    // Gradient based on primary
    gradient: `linear-gradient(135deg, #1976d2 0%, ${alpha(
      "#1976d2",
      0.8
    )} 100%)`,
    cardGradient: `linear-gradient(145deg, ${surface} 0%, ${background} 100%)`,
  };
})();

const feedbackOptions = [
  { label: "Question is unclear", icon: <HelpOutlineIcon fontSize="small" /> },
  { label: "Content is outdated", icon: <UpdateIcon fontSize="small" /> },
  {
    label: "Wrong difficulty level",
    icon: <SignalCellularAltIcon fontSize="small" />,
  },
  { label: "Other", icon: <MoreHorizIcon fontSize="small" /> },
];

const MarkdownQuestion = ({ text }) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => (
          <Typography
            sx={{
              fontSize: { xs: "15px", md: "16px" },
              fontWeight: 600,
              lineHeight: 1.6,
              color: themeColors.textPrimary,
              mb: 1.5,
            }}
          >
            {children}
          </Typography>
        ),
        ol: ({ children }) => (
          <Box
            component="ol"
            sx={{
              pl: { xs: 2.5, md: 3 },
              mb: 1.5,
              mt: 1,
              fontSize: { xs: "15px", md: "16px" },
              lineHeight: 1.7,
            }}
          >
            {children}
          </Box>
        ),
        li: ({ children }) => (
          <Typography
            component="li"
            sx={{
              mb: 1,
              lineHeight: 1.7,
              color: themeColors.textPrimary,
            }}
          >
            {children}
          </Typography>
        ),
        code({ inline, children }) {
          const value = String(children);
          const isBlock = !inline && value.includes("\n");

          if (!isBlock) {
            return (
              <Box
                component="span"
                sx={{
                  backgroundColor: "#d8d9ddff",
                  color: themeColors.primaryDark,
                  borderRadius: "6px",
                  fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                  fontSize: { xs: "13px", md: "14px" },
                  px: 1,
                  py: 0.3,
                  mx: 0.5,
                }}
              >
                {value}
              </Box>
            );
          }

          return (
            <Box
              sx={{
                backgroundColor: "#1e293b",
                color: "#f1f5f9",
                p: { xs: 1.5, md: 2 },
                my: 2,
                borderRadius: "8px",
                overflowX: "auto",
                fontFamily: "'JetBrains Mono', 'Courier New', monospace",
                fontSize: { xs: "12px", md: "13px" },
                border: "1px solid #334155",
              }}
            >
              <pre style={{ margin: 0 }}>
                <code>{value}</code>
              </pre>
            </Box>
          );
        },
      }}
    >
      {text}
    </ReactMarkdown>
  );
};

export const QuetionPaperPreview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { assessment_Id, initial, gen_Id, fromView } = location?.state || {};
  const { showSnackbar } = useSnackbar();

  const [conversation, setConversation] = useState(() => {
    const saved = localStorage.getItem("conversation");
    return saved ? JSON.parse(saved) : [];
  });
  const [assessmentId, setAssessmentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedQuestionPara, setSelectedQuestionPara] = useState(null);
  const [selectedIssue, setSelectedIssue] = useState("");
  const [additionalComments, setAdditionalComments] = useState("");
  const [genId, setGenId] = useState(
    gen_Id ? gen_Id : localStorage?.getItem("gen_id") || null
  );
  const [createdDate, setCreatedDate] = useState(null);
  const [generate, setGenerate] = useState(true);

  const handleThumbClick = (id, question) => {
    setSelectedQuestion(id);
    setSelectedQuestionPara(question);
    setSelectedIssue("");
    setAdditionalComments("");
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setTimeout(() => {
      setSelectedQuestion(null);
      setSelectedIssue("");
      setAdditionalComments("");
    }, 300);
  };

  const handleSubmitFeedback = () => {
    const feedbackText = `${selectedIssue}${additionalComments ? ": " + additionalComments : ""
      }`;

    const updatedConversation = conversation.map((q) =>
      q.question_id === selectedQuestion ? { ...q, feedback: feedbackText } : q
    );

    setConversation(updatedConversation);
    localStorage.setItem("conversation", JSON.stringify(updatedConversation));

    showSnackbar("success", "Feedback submitted successfully!");
    handleDialogClose();
  };

  const previewQuestions = async () => {
    setLoading(true);
    try {
      // Prepare feedback response
      const feedbackResponse = `${selectedIssue}${additionalComments ? ": " + additionalComments : ""
        }`;

      const payload = {
        gen_id: genId,
        question_id: selectedQuestion || null,
        feedback: feedbackResponse || "",
      };

      const res = await api.post("/RefineQuestions", payload);
      if (res?.data?.status === "Success") {
        localStorage?.removeItem("gen_id");
        setConversation(res?.data?.questions);
        localStorage.setItem(
          "conversation",
          JSON.stringify(res.data.questions)
        );
        setGenId(res?.data?.gen_id);
        setCreatedDate(res?.data?.created_at);
        localStorage?.setItem("gen_id", res?.data?.gen_id);
        setGenerate(false);
        showSnackbar("success", "Questions refined successfully!");
      } else {
        showSnackbar(
          "error",
          res?.data?.message || "Failed to refine questions"
        );
      }
    } catch (error) {
      console.error("Error refining questions:", error);
      showSnackbar("error", "Failed to refine questions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const payload = {
        assessment_id: assessment_Id || null,
      };
      const res = await api.post("PreviewQuestions/", payload);
      if (res?.data?.status === "success") {
        setConversation(res?.data?.questions);
        localStorage.setItem(
          "conversation",
          JSON.stringify(res.data.questions)
        );
        setAssessmentId(res?.data?.assessment_id);
        setGenId(res?.data?.gen_id);
        navigate(location?.pathname, {
          replace: true,
          state: {
            ...location?.state,
            initial: null,
          },
        });
        setGenerate(true);
        showSnackbar("success", "Questions loaded successfully!");
      } else {
        showSnackbar("error", res?.data?.message || "Failed to load questions");
        navigate(-1);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      showSnackbar("error", "Failed to load questions. Please try again.");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const verifyQuestions = async () => {
    setLoading(true);
    try {
      const payload = {
        gen_id: genId,
        assessment_id: assessmentId,
        questions: conversation,
      };

      const res = await api.post("VerifyPreviewQuestions", payload);

      if (res?.data?.status === "success") {
        localStorage.removeItem("conversation");
        localStorage.removeItem("gen_id");
        showSnackbar(
          "success",
          res?.data?.message || "Questions verified successfully!"
        );
        navigate(-1);
      } else {
        showSnackbar(
          "error",
          res?.data?.message || "Failed to verify questions"
        );
      }
    } catch (error) {
      console.error("Error verifying questions:", error);
      showSnackbar("error", "Failed to verify questions!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initial === true) {
      fetchQuestions();
    } else if (fromView === true) {
      previewQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        pb: 6,
      }}
    >
      {loading ? (
        <OverAllLoader message="Fetching preview..." />
      ) : (
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Back Button */}
          {fromView === true && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ mb: 3, color: "#1c97e3", fontWeight: 600, textTransform: "none" }}
            >
              Back to Builder List
            </Button>
          )}

          {/* Header Section */}
          <Fade in timeout={700}>
            <Paper
              elevation={0}
              sx={{
                background: themeColors.cardGradient,
                borderRadius: { xs: "16px", md: "24px" },
                p: { xs: 3, md: 4 },
                mb: { xs: 3, md: 4 },
                border: `1px solid ${themeColors.border}`,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: themeColors.gradient,
                },
              }}
            >
              <Grid
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Grid item xs={12} md={fromView ? 12 : 8}>
                  <Box display="flex" alignItems="center" gap={2} mb={1.5}>
                    <Box
                      sx={{
                        background: `linear-gradient(135deg, #1976d2 0%, ${alpha(
                          "#1976d2",
                          0.8
                        )} 100%)`,
                        borderRadius: "14px",
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <DescriptionOutlinedIcon
                        sx={{
                          fontSize: { xs: 28, md: 32 },
                          color: "#fff",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: { xs: "24px", md: "30px" },
                          fontWeight: 800,
                          background: themeColors.gradient,
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                        }}
                      >
                        Question Paper Preview
                      </Typography>
                      <Typography
                        sx={{
                          color: themeColors.textSecondary,
                          fontSize: { xs: "14px", md: "16px" },
                          mt: 0.5,
                        }}
                      >
                        {conversation?.length || 0} questions {" "}
                        {fromView !== true && (
                          <Box
                            component="span"
                            sx={{
                              color: themeColors.primary,
                              fontWeight: 600,
                            }}
                          >
                            • Click thumbs down to give feedback
                          </Box>
                        )}
                      </Typography>
                      {fromView === true && createdDate && (
                        <Typography
                          sx={{
                            color: themeColors.textSecondary,
                            fontSize: "14px",
                            mt: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <UpdateIcon fontSize="small" />
                          Created:{" "}
                          {dayjs(createdDate).format("MMM DD, YYYY hh:mm A")}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* Action Buttons */}
                {fromView !== true && (
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 2,
                        justifyContent: { md: "flex-end" },
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={verifyQuestions}
                        startIcon={<CheckCircleIcon />}
                        sx={{
                          background: themeColors.success,
                          color: "#fff",
                          textTransform: "none",
                          borderRadius: "12px",
                          px: { xs: 2, md: 3 },
                          py: 1.5,
                          fontWeight: 600,
                          fontSize: "15px",
                          boxShadow: "0 4px 14px rgba(46, 125, 50, 0.3)",
                          "&:hover": {
                            background: "#1b5e20",
                            transform: "translateY(-2px)",
                            boxShadow: "0 6px 20px rgba(46, 125, 50, 0.4)",
                          },
                          transition: "all 0.3s ease",
                          flex: { xs: 1, sm: "none" },
                        }}
                      >
                        Verify Questions
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => navigate(-1)}
                        startIcon={<CancelIcon />}
                        sx={{
                          background: themeColors.error,
                          color: "#fff",
                          textTransform: "none",
                          borderRadius: "12px",
                          px: { xs: 2, md: 3 },
                          py: 1.5,
                          fontWeight: 600,
                          fontSize: "15px",
                          boxShadow: "0 4px 14px rgba(211, 47, 47, 0.3)",
                          "&:hover": {
                            background: "#b71c1c",
                            transform: "translateY(-2px)",
                            boxShadow: "0 6px 20px rgba(211, 47, 47, 0.4)",
                          },
                          transition: "all 0.3s ease",
                          flex: { xs: 1, sm: "none" },
                        }}
                      >
                        Reject
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Fade>

          {/* Questions List */}
          <Grid>
            {conversation.map((q, index) => (
              <Grid
                item
                xs={12}
                key={q.question_id || q.id || index}
                sx={{ mb: 2 }}
              >
                <Zoom in timeout={800 + index * 100}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: { xs: "16px", md: "20px" },
                      background: themeColors.cardGradient,
                      p: { xs: 2.5, md: 3.5 },
                      border: `1px solid ${themeColors.border}`,
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      position: "relative",
                      overflow: "hidden",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: `
                          0 20px 40px rgba(0, 0, 0, 0.1),
                          0 0 0 1px rgba(25, 118, 210, 0.1)
                        `,
                        borderColor: themeColors.primaryLight,
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "6px",
                        height: "100%",
                        background: themeColors.gradient,
                        borderTopLeftRadius: "20px",
                        borderBottomLeftRadius: "20px",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        gap: { xs: 2, md: 3 },
                        pl: { xs: 1, md: 2 },
                      }}
                    >
                      {/* Question Content */}
                      <Box sx={{ flex: 1 }}>
                        {/* Question Number */}
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: themeColors.gradient,
                            color: "#fff",
                            width: { xs: 32, md: 40 },
                            height: { xs: 32, md: 40 },
                            borderRadius: "12px",
                            mb: 2.5,
                            fontWeight: 700,
                            fontSize: { xs: "16px", md: "18px" },
                            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                          }}
                        >
                          {index + 1}
                        </Box>

                        {/* Question Text */}
                        <Box sx={{ mb: 3 }}>
                          <MarkdownQuestion text={q.question} />
                        </Box>

                        {/* Image */}
                        {q.image_url && (
                          <Box
                            sx={{
                              mb: 3,
                              borderRadius: "12px",
                              overflow: "hidden",
                              boxShadow: "0 6px 20px rgba(0, 0, 0, 0.1)",
                              maxWidth: { xs: "100%", md: "400px" },
                            }}
                          >
                            <Box
                              component="img"
                              alt="Question diagram"
                              src={`data:image/png;base64,${q.image_url}`}
                              sx={{
                                width: "100%",
                                height: "auto",
                                display: "block",
                              }}
                            />
                          </Box>
                        )}

                        {/* Options */}
                        {q.options && (
                          <Box
                            sx={{
                              mt: 3,
                              pt: 2.5,
                              borderTop: `1px dashed ${themeColors.border}`,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "16px",
                                fontWeight: 700,
                                color: themeColors.textPrimary,
                                mb: 2,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  background: themeColors.gradient,
                                  color: "#fff",
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: "6px",
                                  fontSize: "14px",
                                }}
                              >
                                Options
                              </Box>
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "16px",
                                lineHeight: 1.8,
                                color: themeColors.textPrimary,
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {q.options}
                            </Typography>
                          </Box>
                        )}

                        {/* Feedback Card */}
                        {q.feedback && (
                          <Fade in timeout={500}>
                            <Box
                              sx={{
                                mt: 3,
                                p: 2.5,
                                background: themeColors.successLight,
                                borderRadius: "14px",
                                border: `1px solid #a5d6a7`,
                                animation: "slideIn 0.5s ease",
                                "@keyframes slideIn": {
                                  from: {
                                    opacity: 0,
                                    transform: "translateY(10px)",
                                  },
                                  to: {
                                    opacity: 1,
                                    transform: "translateY(0)",
                                  },
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1.5,
                                }}
                              >
                                <FeedbackIcon
                                  sx={{
                                    color: themeColors.success,
                                    fontSize: 20,
                                    mt: 0.5,
                                  }}
                                />
                                <Box>
                                  <Typography
                                    sx={{
                                      fontWeight: 700,
                                      color: themeColors.success,
                                      mb: 0.5,
                                      fontSize: "15px",
                                    }}
                                  >
                                    ✓ Feedback Submitted
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      color: "#2e7d32",
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {q.feedback}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Fade>
                        )}
                      </Box>

                      {/* Action Sidebar */}
                      {fromView !== true && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "row", md: "column" },
                            alignItems: { xs: "center", md: "flex-end" },
                            justifyContent: {
                              xs: "space-between",
                              md: "flex-start",
                            },
                            gap: { xs: 2, md: 1 },
                            pt: { xs: 2, md: 0 },
                            borderTop: {
                              xs: `1px solid ${themeColors.border}`,
                              md: "none",
                            },
                          }}
                        >
                          {/* Thumbs Down Button */}
                          <IconButton
                            onClick={() =>
                              !q.feedback &&
                              handleThumbClick(q.question_id, q.question)
                            }
                            disabled={!!q.feedback}
                            sx={{
                              background: q.feedback
                                ? themeColors.errorLight
                                : themeColors.gradient,
                              color: q.feedback ? themeColors.error : "#fff",
                              width: { xs: 48, md: 56 },
                              height: { xs: 48, md: 56 },
                              borderRadius: "16px",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                background: q.feedback
                                  ? themeColors.errorLight
                                  : themeColors.primary,
                                color: "#fff",
                                transform: "scale(1.1)",
                                boxShadow: q.feedback
                                  ? "0 6px 20px rgba(211, 47, 47, 0.3)"
                                  : "0 6px 20px rgba(25, 118, 210, 0.3)",
                              },
                              "&.Mui-disabled": {
                                background: themeColors.errorLight,
                                color: themeColors.error,
                              },
                            }}
                            title={
                              q.feedback
                                ? "Feedback already submitted"
                                : "Give feedback"
                            }
                          >
                            <ThumbDownAltOutlinedIcon
                              sx={{ fontSize: { xs: 22, md: 24 } }}
                            />
                          </IconButton>
                        </Box>
                      )}

                      {/* View Mode Date */}
                      {fromView === true && q.created_at && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "start",
                            gap: 1,
                            color: themeColors.textSecondary,
                            fontSize: "14px",
                            mt: { xs: 2, md: 0 },
                          }}
                        >
                          <UpdateIcon fontSize="small" />
                          <Typography>
                            {dayjs(q.created_at).format("MMM DD, YYYY hh:mm A")}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Zoom>
              </Grid>
            ))}
          </Grid>

          {/* Empty State */}
          {conversation.length === 0 && !loading && (
            <Box
              sx={{
                textAlign: "center",
                py: 10,
                px: 3,
              }}
            >
              <DescriptionOutlinedIcon
                sx={{ fontSize: 80, color: themeColors.border, mb: 3 }}
              />
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: 600,
                  color: themeColors.textSecondary,
                  mb: 1,
                }}
              >
                No questions available
              </Typography>
              <Typography sx={{ color: themeColors.textSecondary }}>
                Start by creating or loading questions
              </Typography>
            </Box>
          )}

          {/* Feedback Dialog */}
          <Dialog
            open={openDialog}
            onClose={handleDialogClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                borderRadius: { xs: "16px", md: "24px" },
                background: themeColors.surface,
                overflow: "hidden",
                m: { xs: 2, md: 3 },
                width: "100%",
                maxWidth: { xs: "calc(100% - 32px)", md: "540px" },
              },
            }}
          >
            {/* Dialog Header */}
            <DialogTitle
              sx={{
                p: { xs: 2.5, md: 3 },
                pb: 1,
                background: themeColors.gradient,
                color: "#fff",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <FeedbackIcon />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "18px", md: "20px" },
                    }}
                  >
                    Provide Feedback
                  </Typography>
                </Box>
                <IconButton
                  aria-label="Close"
                  onClick={handleDialogClose}
                  size="small"
                  sx={{
                    color: "#fff",
                    "&:hover": { background: "rgba(255,255,255,0.1)" },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Selected Question Preview */}
              <Box
                sx={{
                  background: "rgba(255, 255, 255, 0.1)",
                  p: 2,
                  borderRadius: "12px",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontSize: "14px",
                    lineHeight: 1.5,
                    opacity: 0.9,
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {selectedQuestionPara?.split("\n")[0]}
                </Typography>
              </Box>
            </DialogTitle>

            <Divider />

            {/* Dialog Content */}
            <DialogContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: themeColors.textPrimary,
                  mb: 2.5,
                  fontSize: "16px",
                }}
              >
                Select an issue type
              </Typography>

              {/* Issue Chips */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 1.5,
                  mb: 3,
                }}
              >
                {feedbackOptions.map((option) => (
                  <Chip
                    key={option.label}
                    icon={option.icon}
                    label={option.label}
                    clickable
                    onClick={() => {
                      setSelectedIssue(option.label);
                      if (option.label !== "Other") setAdditionalComments("");
                    }}
                    variant={
                      selectedIssue === option.label ? "filled" : "outlined"
                    }
                    sx={{
                      borderRadius: "12px",
                      py: 1.5,
                      fontSize: "14px",
                      fontWeight: 500,
                      justifyContent: "flex-start",
                      borderColor:
                        selectedIssue === option.label
                          ? themeColors.primary
                          : themeColors.border,
                      background:
                        selectedIssue === option.label
                          ? themeColors.gradient
                          : "transparent",
                      color:
                        selectedIssue === option.label
                          ? "#fff"
                          : themeColors.textPrimary,
                      "& .MuiChip-icon": {
                        color:
                          selectedIssue === option.label
                            ? "#fff"
                            : themeColors.textSecondary,
                      },
                      "&:hover": {
                        background:
                          selectedIssue === option.label
                            ? themeColors.gradient
                            : "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  />
                ))}
              </Box>

              {/* Additional Comments */}
              {selectedIssue === "Other" && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: themeColors.textPrimary,
                      mb: 1.5,
                      fontSize: "15px",
                    }}
                  >
                    Additional Comments
                  </Typography>
                  <TextField
                    placeholder="Please describe the issue in detail..."
                    multiline
                    rows={4}
                    fullWidth
                    value={additionalComments}
                    onChange={(e) => setAdditionalComments(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        background: themeColors.background,
                        "& fieldset": {
                          borderColor: themeColors.border,
                        },
                        "&:hover fieldset": {
                          borderColor: themeColors.primary,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: themeColors.primary,
                          borderWidth: "2px",
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </DialogContent>

            {/* Dialog Actions */}
            <DialogActions
              sx={{
                p: { xs: 2, md: 3 },
                pt: 1,
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Button
                onClick={handleDialogClose}
                variant="outlined"
                fullWidth={isMobile}
                sx={{
                  borderRadius: "12px",
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  borderColor: themeColors.border,
                  color: themeColors.textSecondary,
                  "&:hover": {
                    borderColor: themeColors.primary,
                    background: themeColors.primaryLight,
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitFeedback}
                variant="contained"
                disabled={
                  !selectedIssue ||
                  (selectedIssue === "Other" && !additionalComments.trim())
                }
                startIcon={<SendOutlinedIcon />}
                fullWidth={isMobile}
                sx={{
                  borderRadius: "12px",
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  background: themeColors.gradient,
                  boxShadow: "0 4px 14px rgba(102, 126, 234, 0.4)",
                  "&:hover": {
                    boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                    transform: "translateY(-2px)",
                  },
                  "&.Mui-disabled": {
                    background: themeColors.border,
                    color: themeColors.textSecondary,
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Submit Feedback
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      )}
    </Box>
  );
};

export default QuetionPaperPreview;
